import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import DeleteAccount from '../components/DeleteAccount';

const MONTHS_RU = ['Январь','Февраль','Март','Апрель','Май','Июнь',
                   'Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];

export default function DashboardPage({ onLogout, onDelete, free = false }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    birth_date: '',
    birth_time: '',
    birth_country: '',
    birth_city: '',
    birth_lat: '',
    birth_lon: '',
    utc_offset: '0',
    start_date: '',
    end_date: '',
  });

  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');

  const cityDropdownRef = useRef(null);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const [freeUsed, setFreeUsed] = useState(false);
  useEffect(() => {
    if (free) api.freeStatus().then(r => setFreeUsed(!!r.data.used)).catch(() => {});
  }, [free]);

  // Load countries on mount
  useEffect(() => {
    loadCountries();
  }, []);

  // Load cities when country changes
  useEffect(() => {
    if (form.birth_country) {
      loadCitiesByCountry(form.birth_country);
    } else {
      setCities([]);
      setFilteredCities([]);
    }
  }, [form.birth_country]);

  const loadCountries = async () => {
    try {
      const response = await api.countries();
      setCountries(response.data);
      console.log('✓ Страны загружены:', response.data.length);
    } catch (err) {
      console.error('✗ Ошибка загрузки стран:', err);
      setError('Не удалось загрузить список стран');
    }
  };

  const loadCitiesByCountry = async (countryCode) => {
    try {
      setGeoLoading(true);
      const response = await api.citiesByCountry(countryCode);
      setCities(response.data);
      setFilteredCities(response.data.slice(0, 10)); // Show top 10
      console.log(`✓ Города для ${countryCode} загружены:`, response.data.length);
    } catch (err) {
      console.error('✗ Ошибка загрузки городов:', err);
      setCities([]);
      setFilteredCities([]);
    } finally {
      setGeoLoading(false);
    }
  };

  const searchTimer = useRef(null);

  const handleCitySearch = (searchText) => {
    set('birth_city', searchText);
    setGeoError('');
    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (!searchText || searchText.trim().length < 1) {
      setFilteredCities(cities.slice(0, 10));
      setShowCityDropdown(false);
      return;
    }

    // Поиск на сервере (понимает кириллицу: «Дніпро», «Днепр», «Харків»)
    searchTimer.current = setTimeout(async () => {
      try {
        const response = await api.citiesByCountry(form.birth_country, searchText.trim());
        const found = response.data.slice(0, 20);
        setFilteredCities(found);
        setShowCityDropdown(found.length > 0);
        if (found.length === 0) setGeoError('Город не найден. Попробуйте другое написание.');
      } catch (err) {
        setFilteredCities([]);
        setShowCityDropdown(false);
      }
    }, 250);
  };

  const [birthTz, setBirthTz] = useState('');

  // Точный пояс на дату и время рождения (летнее время, исторические изменения) — считает сервер
  const refreshOffset = async (tz, date, time) => {
    if (!tz || !date) return;
    try {
      const r = await api.utcOffset(tz, date, time);
      set('utc_offset', String(r.data.utc_offset));
    } catch (e) { /* оставляем значение, которое выбрал пользователь */ }
  };

  useEffect(() => {
    refreshOffset(birthTz, form.birth_date, form.birth_time);
  }, [birthTz, form.birth_date, form.birth_time]);

  const selectCity = (city) => {
    set('birth_city', city.name);
    setBirthTz(city.timezone);
    setForm(f => ({
      ...f,
      birth_lat: city.latitude.toString(),
      birth_lon: city.longitude.toString(),
    }));
    setShowCityDropdown(false);
    console.log('✓ Город выбран:', city.name, `(${city.latitude}, ${city.longitude})`);
  };

  const calculateUTCOffset = (timezone, birthDate) => {
    // Simplified: just use timezone rules
    // In real implementation, backend should handle this
    const tzOffsets = {
      'Europe/Kyiv': 3,
      'Europe/Moscow': 3,
      'Asia/Dubai': 4,
      'Asia/Bangkok': 7,
      'Asia/Tokyo': 9,
      'Europe/London': 0,
      'Europe/Paris': 1,
      'America/New_York': -5,
      'America/Los_Angeles': -8,
    };
    return tzOffsets[timezone] || 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // Convert data types for API
      const calcData = {
        birth_date: form.birth_date,
        birth_time: form.birth_time || '12:00',
        birth_lat: parseFloat(form.birth_lat),
        birth_lon: parseFloat(form.birth_lon),
        utc_offset: parseFloat(form.utc_offset),
        start_date: form.start_date,
        end_date: form.end_date,
        client_name: form.name,
        birth_country: form.birth_country,
        birth_city: form.birth_city,
      };

      console.log('📤 Отправляю данные:', calcData);
      sessionStorage.setItem('calcForm', JSON.stringify(calcData));
      navigate(free ? '/free-calendar' : '/calendar');
    } catch (err) {
      console.error('✗ Ошибка:', err);
      setError('Ошибка при подготовке данных');
    } finally {
      setLoading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div style={styles.page}>
      {/* Шапка */}
      <header style={styles.header}>
        <div style={styles.logo}>✦ AstraData</div>
        {free ? (
          <button onClick={() => navigate('/')} aria-label="Закрыть"
            style={{width:44,height:44,borderRadius:'50%',border:'1px solid #3a3a5c',background:'#22223a',color:'#fff',fontSize:22,cursor:'pointer'}}>✕</button>
        ) : (
          <button className="btn btn-outline" onClick={onLogout} style={{padding:'8px 16px',fontSize:13}}>
            Выйти
          </button>
        )}
      </header>

      <main style={styles.main}>
        <div style={styles.hero}>
          <h1 style={styles.heroTitle}>Найдите свой день для покупки недвижимости</h1>
          <p style={styles.heroSub}>
            Ведическая астрология — Панчанга, транзиты, Вимшоттари Даша
          </p>
        </div>

        {free && (
          <div className="card" style={{border:'1px solid #c9a84c',background:'rgba(201,168,76,0.08)',marginBottom:16,textAlign:'center'}}>
            {freeUsed ? (
              <>
                <h3 style={{marginBottom:8}}>Бесплатное сканирование уже использовано</h3>
                <p style={{fontSize:14,color:'#a0a0c0',marginBottom:12}}>Создайте кабинет и откройте неограниченные расчёты, PDF-отчёт и ИИ-объяснения каждого дня.</p>
                <button className="btn btn-gold" onClick={() => navigate('/register')}>Зарегистрироваться</button>
              </>
            ) : (
              <>
                <h3 style={{marginBottom:6}}>Бесплатное сканирование — без регистрации</h3>
                <p style={{fontSize:14,color:'#a0a0c0'}}>Одно сканирование на устройство, период до 2 месяцев. Результат покажем сразу. Затем можно создать кабинет — и считать без ограничений.</p>
              </>
            )}
          </div>
        )}
        <div className="card" style={{...styles.formCard, ...(free && freeUsed ? {opacity:0.45,pointerEvents:'none'} : {})}}>
          <h2 style={styles.formTitle}>Данные для расчёта</h2>

          <form onSubmit={submit} style={styles.form}>

            {/* Имя */}
            <div className="input-group">
              <label>Имя клиента</label>
              <input className="input" placeholder="Например: Анна Иванова"
                value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>

            {/* Дата и время рождения */}
            <div style={styles.row}>
              <div className="input-group" style={{flex:1}}>
                <label>Дата рождения</label>
                <input className="input" type="date" data-ph="ДД.ММ.РРРР" data-empty={!form.birth_date}
                  value={form.birth_date} onChange={e => set('birth_date', e.target.value)} required />
              </div>
              <div className="input-group" style={{flex:1}}>
                <label>Время <span style={{color:'#555570'}}>(если знаете)</span></label>
                <input className="input" type="time" data-ph="ЧЧ:ММ" data-empty={!form.birth_time}
                  value={form.birth_time} onChange={e => set('birth_time', e.target.value)} />
              </div>
            </div>

            {/* Страна и Город */}
            <div style={styles.row}>
              <div className="input-group" style={{flex:1}}>
                <label>Страна рождения</label>
                <select className="input" value={form.birth_country}
                  onChange={e => set('birth_country', e.target.value)} required>
                  <option value="">Выберите страну...</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>
                      {c.name} ({c.cities_count})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Город с автодополнением */}
            {form.birth_country && (
              <div className="input-group" ref={cityDropdownRef} style={{position:'relative'}}>
                <label>Город рождения</label>
                <div style={{position:'relative'}}>
                  <input className="input"
                    placeholder="Начните вводить название города..."
                    value={form.birth_city}
                    onChange={e => handleCitySearch(e.target.value)}
                    onFocus={() => setShowCityDropdown(true)}
                    required />

                  {geoLoading && <span style={{fontSize:12, color:'#666680', marginTop:4}}>⏳ Загружаю города...</span>}
                  {geoError && <span style={{fontSize:12, color:'#e74c3c', marginTop:4}}>{geoError}</span>}

                  {/* Dropdown with cities */}
                  {showCityDropdown && filteredCities.length > 0 && (
                    <div style={styles.dropdown}>
                      {filteredCities.map((city, idx) => (
                        <div key={idx} style={styles.dropdownItem} onClick={() => selectCity(city)}>
                          <div style={{fontWeight:500}}>{city.name}</div>
                          <div style={{fontSize:11, color:'#888'}}>
                            {city.latitude.toFixed(2)}°N, {city.longitude.toFixed(2)}°E • {city.timezone}
                          </div>
                          {city.population > 0 && (
                            <div style={{fontSize:10, color:'#666'}}>
                              Население: {(city.population / 1000).toFixed(0)}k
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {showCityDropdown && filteredCities.length === 0 && form.birth_city && (
                    <div style={styles.dropdown}>
                      <div style={{padding:10, textAlign:'center', color:'#888'}}>
                        Города не найдены
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Координаты */}
            <div style={styles.row}>
              <div className="input-group" style={{flex:1}}>
                <label>Широта {form.birth_lat && <span style={{color:'#a78bfa', fontSize:11}}>✓ заполнено</span>}</label>
                <input className="input" type="number" step="0.0001" placeholder="55.7558"
                  value={form.birth_lat} onChange={e => set('birth_lat', e.target.value)} required />
              </div>
              <div className="input-group" style={{flex:1}}>
                <label>Долгота {form.birth_lon && <span style={{color:'#a78bfa', fontSize:11}}>✓ заполнено</span>}</label>
                <input className="input" type="number" step="0.0001" placeholder="37.6173"
                  value={form.birth_lon} onChange={e => set('birth_lon', e.target.value)} required />
              </div>
              <div className="input-group" style={{width:120}}>
                <label>Часовой пояс {form.utc_offset && form.utc_offset !== '0' && <span style={{color:'#a78bfa', fontSize:11}}>✓ заполнено</span>}</label>
                <select className="input" value={form.utc_offset} onChange={e => set('utc_offset', e.target.value)}>
                  {Array.from({length:53}, (_,i) => -12 + i*0.5).map(h => (
                    <option key={h} value={h}>
                      UTC{h>=0?'+':'-'}{Math.floor(Math.abs(h))}{Math.abs(h)%1 ? ':30' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <p style={styles.hint}>
              Координаты заполняются автоматически при выборе города из списка.
            </p>

            {/* Период */}
            <div style={styles.divider} />
            <h3 style={styles.sectionTitle}>Период для анализа</h3>

            <div style={styles.row}>
              <div className="input-group" style={{flex:1}}>
                <label>С даты</label>
                <input className="input" type="date" data-ph="ДД.ММ.РРРР" data-empty={!form.start_date}
                  value={form.start_date} onChange={e => set('start_date', e.target.value)} required />
              </div>
              <div className="input-group" style={{flex:1}}>
                <label>По дату <span style={{color:'#555570'}}>({free ? 'макс. 2 мес.' : 'макс. 6 мес.'})</span></label>
                <input className="input" type="date" data-ph="ДД.ММ.РРРР" data-empty={!form.end_date}
                  value={form.end_date} onChange={e => set('end_date', e.target.value)} required />
              </div>
            </div>

            {error && <p style={{color:'#e74c3c', fontSize:13}}>{error}</p>}

            <button className="btn btn-primary" type="submit" disabled={loading}
              style={{width:'100%', padding:'16px', fontSize:16, marginTop:8}}>
              {loading ? '⏳ Считаем звёзды...' : '✦ Рассчитать благоприятные даты'}
            </button>
          </form>
        </div>

        {/* Подсказки */}
        <div style={styles.tips}>
          {[
            { icon: '🌙', text: 'Анализ Панчанги — Титхи, Накшатра, Вара, Йога, Карана' },
            { icon: '♃',  text: 'Транзиты Юпитера, Сатурна и ретроградных планет' },
            { icon: '⏳', text: 'Вимшоттари Даша — ваш текущий жизненный период' },
            { icon: '📄', text: 'PDF-отчёт с топ-5 датами для клиента' },
          ].map((t, i) => (
            <div key={i} style={styles.tipCard}>
              <span style={styles.tipIcon}>{t.icon}</span>
              <span style={styles.tipText}>{t.text}</span>
            </div>
          ))}
        </div>
        {!free && onDelete && <DeleteAccount onDelete={onDelete} />}
      </main>
    </div>
  );
}

const styles = {
  page:        { minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  header:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px', borderBottom: '1px solid #2a2a4a', background: '#0f0f1a' },
  logo:        { fontSize: 20, fontWeight: 700, color: '#a78bfa' },
  main:        { flex: 1, maxWidth: 760, margin: '0 auto', padding: '40px 16px', width: '100%' },
  hero:        { textAlign: 'center', marginBottom: 36 },
  heroTitle:   { fontSize: 28, fontWeight: 700, marginBottom: 10, background: 'linear-gradient(135deg,#a78bfa,#60a5fa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  heroSub:     { fontSize: 15, color: '#666680' },
  formCard:    { marginBottom: 32 },
  formTitle:   { fontSize: 18, fontWeight: 600, marginBottom: 24 },
  form:        { display: 'flex', flexDirection: 'column', gap: 16 },
  row:         { display: 'flex', gap: 12, flexWrap: 'wrap' },
  hint:        { fontSize: 12, color: '#555570', lineHeight: 1.5 },
  divider:     { borderTop: '1px solid #2a2a4a', margin: '4px 0' },
  sectionTitle:{ fontSize: 15, fontWeight: 600, color: '#a0a0c0' },
  tips:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 },
  tipCard:     { background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 12, padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 8 },
  tipIcon:     { fontSize: 22 },
  tipText:     { fontSize: 12, color: '#a0a0c0', lineHeight: 1.5 },
  dropdown:    {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: '#1a1a2e',
    border: '1px solid #2a2a4a',
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 300,
    overflowY: 'auto',
    zIndex: 10,
    boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
  },
  dropdownItem: {
    padding: '12px 16px',
    borderBottom: '1px solid #2a2a4a',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
};

// Add hover effect to dropdown items
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  [style*="padding: 12px 16px"][style*="cursor: pointer"]:hover {
    background: #2a2a4a !important;
  }
`;
document.head.appendChild(styleSheet);
